import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  TextField,
} from "@mui/material";
import { Formik } from "formik";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";

import DistributionNetworkApi from "../../../../../infrastructure/api/distributionNetworkApi";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import AreaCitiesAutocomplete from "../../shared/distribution/AreaCitiesAutocomplete";

const CompanyCitiesField = ({ companyId, value, selectedItems, onChange }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!companyId) {
      setOptions([]);
      return undefined;
    }
    setLoading(true);
    DistributionNetworkApi.getCompanyCities(companyId).then((res) => {
      if (cancelled) return;
      setOptions(res.ok ? res.data || [] : []);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  return (
    <AreaCitiesAutocomplete
      options={options}
      selectedItems={selectedItems}
      value={value}
      onChange={onChange}
      loading={loading}
      disabled={!companyId}
    />
  );
};

const validationSchema = Yup.object().shape({
  distributionCompanyId: Yup.number().min(1, "شرکت توزیع الزامی است").required(),
  name: Yup.string().required("نام امور برق الزامی است"),
});

const empty = {
  id: 0,
  distributionCompanyId: "",
  name: "",
  code: "",
  countyName: "",
  city: "",
  address: "",
  phone: "",
  managerName: "",
  managerPhone: "",
  status: 2,
  engineeringUnitEnabled: false,
  cityIds: [],
  cities: [],
};

const DistributionAreaModifyPage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isCreate = !id || id === "create";
  const navigate = useNavigate();
  const [initial, setInitial] = useState({
    ...empty,
    distributionCompanyId: searchParams.get("companyId")
      ? Number(searchParams.get("companyId"))
      : "",
  });
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const lookups = await DistributionNetworkApi.getCompanyLookups();
      if (!cancelled) setCompanies(lookups.data || []);
      if (!isCreate) {
        const res = await DistributionNetworkApi.getArea(id);
        if (!cancelled) {
          if (res.ok && res.data)
            setInitial({
              ...empty,
              ...res.data,
              cityIds: res.data.cityIds || [],
              cities: res.data.cities || [],
            });
          else toast.error(res.message || "امور برق یافت نشد");
        }
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isCreate]);

  const onSubmit = async (values) => {
    setSaving(true);
    try {
      const { cities, ...rest } = values;
      const res = await DistributionNetworkApi.saveArea({
        ...rest,
        id: isCreate ? 0 : Number(id),
        distributionCompanyId: Number(values.distributionCompanyId),
        cityIds: (values.cityIds || []).map(Number),
      });
      if (res.ok) {
        toast.success(res.message || "ذخیره شد");
        navigate("/SLN/Management/DistributionAreas");
      } else toast.error(res.message || "خطا در ذخیره");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Grid container justifyContent="center" p={4}>
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>
      <Grid item xs={12}>
        <Card>
          <CardHeader title={isCreate ? "ایجاد امور برق" : "ویرایش امور برق"} />
          <CardContent>
            <Formik
              enableReinitialize
              initialValues={initial}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              {({ values, errors, touched, handleChange, handleSubmit, setFieldValue }) => (
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        name="distributionCompanyId"
                        label="شرکت توزیع"
                        value={values.distributionCompanyId || ""}
                        onChange={handleChange}
                        error={Boolean(
                          touched.distributionCompanyId &&
                            errors.distributionCompanyId
                        )}
                        helperText={
                          touched.distributionCompanyId &&
                          errors.distributionCompanyId
                        }
                      >
                        {companies.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.title}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        name="name"
                        label="نام امور برق"
                        value={values.name || ""}
                        onChange={handleChange}
                        error={Boolean(touched.name && errors.name)}
                        helperText={touched.name && errors.name}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        name="code"
                        label="کد سازمانی"
                        value={values.code || ""}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        name="countyName"
                        label="شهرستان / منطقه (توضیحی)"
                        value={values.countyName || ""}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        name="city"
                        label="شهر (توضیحی)"
                        value={values.city || ""}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <CompanyCitiesField
                        companyId={Number(values.distributionCompanyId) || 0}
                        value={values.cityIds}
                        selectedItems={values.cities}
                        onChange={(ids) => setFieldValue("cityIds", ids)}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        name="phone"
                        label="تلفن"
                        value={values.phone || ""}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        name="managerName"
                        label="نام مدیر"
                        value={values.managerName || ""}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        name="managerPhone"
                        label="تلفن مدیر"
                        value={values.managerPhone || ""}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        name="status"
                        label="وضعیت"
                        value={values.status}
                        onChange={handleChange}
                      >
                        <MenuItem value={1}>پیش‌نویس</MenuItem>
                        <MenuItem value={2}>فعال</MenuItem>
                        <MenuItem value={3}>غیرفعال</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <FormControlLabel
                        control={
                          <Switch
                            name="engineeringUnitEnabled"
                            checked={Boolean(values.engineeringUnitEnabled)}
                            onChange={(e) =>
                              setFieldValue(
                                "engineeringUnitEnabled",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label="فعال‌سازی واحد مهندسی این امور (تأیید ظرفیت/طرح پس از کارشناس امور)"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        name="address"
                        label="آدرس"
                        value={values.address || ""}
                        onChange={handleChange}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={saving}
                        startIcon={saving ? <CircularProgress size={16} /> : null}
                      >
                        ذخیره
                      </Button>
                      <Button
                        sx={{ ml: 1 }}
                        onClick={() =>
                          navigate("/SLN/Management/DistributionAreas")
                        }
                      >
                        انصراف
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Formik>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default DistributionAreaModifyPage;
